import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    Paper,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CreditCard as CardIcon,
    Refresh as RefreshIcon,
    AccountBalance as BankIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { usePlaidLink } from 'react-plaid-link';
import { toast } from 'sonner';
import API from '../../../../../API';

/**
 * Platform Payment Methods Management Section
 *
 * Admin-only interface for managing platform-level payment methods
 * for Stripe payout operations (e.g., flowthrough card/bank account).
 */
function PlatformPaymentMethodsSection() {
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [createForm, setCreateForm] = useState({
        stripe_payment_method_id: '',
        method_type: 'card',
        purpose: 'flowthrough',
        label: '',
        last4: '',
    });
    const [editForm, setEditForm] = useState({ label: '', is_active: true });
    const [linkToken, setLinkToken] = useState(null);
    const queryClient = useQueryClient();

    // Fetch all platform payment methods
    const { data, isLoading, error, refetch } = useQuery(
        ['platformPaymentMethods'],
        () => API.admin.listPlatformPaymentMethods(),
        {
            onError: (err) => {
                toast.error(`Failed to load payment methods: ${err.message}`);
            },
        }
    );

    // Create mutation
    const createMutation = useMutation(
        (formData) => API.admin.createPlatformPaymentMethod(formData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['platformPaymentMethods']);
                toast.success('Payment method created');
                setCreateOpen(false);
                setCreateForm({
                    stripe_payment_method_id: '',
                    method_type: 'card',
                    purpose: 'flowthrough',
                    label: '',
                    last4: '',
                });
            },
            onError: (err) => {
                toast.error(`Failed to create: ${err?.response?.data?.error || err.message}`);
            },
        }
    );

    // Update mutation
    const updateMutation = useMutation(
        ({ methodId, data }) => API.admin.updatePlatformPaymentMethod(methodId, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['platformPaymentMethods']);
                toast.success('Payment method updated');
                setEditOpen(false);
            },
            onError: (err) => {
                toast.error(`Failed to update: ${err?.response?.data?.error || err.message}`);
            },
        }
    );

    // Deactivate mutation
    const deactivateMutation = useMutation(
        (methodId) => API.admin.deactivatePlatformPaymentMethod(methodId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['platformPaymentMethods']);
                toast.success('Payment method deactivated');
                setDeleteOpen(false);
            },
            onError: (err) => {
                toast.error(`Failed to deactivate: ${err?.response?.data?.error || err.message}`);
            },
        }
    );

    // Plaid Link token mutation
    const createLinkTokenMutation = useMutation(
        () => API.admin.createPlaidLinkToken(),
        {
            onSuccess: (response) => {
                setLinkToken(response.link_token);
            },
            onError: (err) => {
                toast.error(`Failed to create Plaid link token: ${err?.response?.data?.error || err.message}`);
            },
        }
    );

    // Plaid payment method creation mutation
    const createPlaidPaymentMethodMutation = useMutation(
        (data) => API.admin.createPlaidPaymentMethod(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['platformPaymentMethods']);
                toast.success('Plaid flowthrough account linked successfully');
            },
            onError: (err) => {
                toast.error(`Failed to link Plaid account: ${err?.response?.data?.error || err.message}`);
            },
        }
    );

    // Plaid Link configuration
    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: (public_token, metadata) => {
            // Extract account info
            const account = metadata.accounts[0];

            // Call backend to exchange token and create payment method
            createPlaidPaymentMethodMutation.mutate({
                public_token: public_token,
                account_id: account.id,
                institution_name: metadata.institution.name,
                label: `Flowthrough Plaid ACH - ${metadata.institution.name}`,
            });
        },
        onExit: (err, metadata) => {
            if (err) {
                console.error('Plaid Link error:', err);
                toast.error('Plaid Link flow was cancelled or encountered an error');
            }
            // Reset link token to allow retry
            setLinkToken(null);
        },
    });

    // Open Plaid Link when token is ready
    useEffect(() => {
        if (linkToken && ready) {
            open();
        }
    }, [linkToken, ready, open]);

    // Handler for "Link Plaid Account" button
    const handleLinkPlaid = () => {
        createLinkTokenMutation.mutate();
    };

    const handleOpenEdit = (method) => {
        setSelectedMethod(method);
        setEditForm({ label: method.label || '', is_active: method.is_active });
        setEditOpen(true);
    };

    const handleOpenDelete = (method) => {
        setSelectedMethod(method);
        setDeleteOpen(true);
    };

    const methods = data?.methods || [];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        <CardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Platform Payment Methods
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage platform flowthrough payment methods for automated payout operations.
                    </Typography>
                </Box>
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<BankIcon />}
                        onClick={handleLinkPlaid}
                        disabled={createLinkTokenMutation.isLoading}
                        sx={{ mr: 1 }}
                    >
                        {createLinkTokenMutation.isLoading ? 'Loading...' : 'Link Plaid Flowthrough'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{ mr: 1 }}
                    >
                        Add Stripe Method
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetch()}
                        disabled={isLoading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Content */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">Failed to load payment methods: {error.message}</Alert>
            ) : methods.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>No Payment Methods</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        No platform payment methods have been configured yet.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                        Add First Method
                    </Button>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Label</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Institution</TableCell>
                                <TableCell>Last 4</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell>Last Used</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {methods.map((method) => (
                                <TableRow key={method.id}>
                                    <TableCell>{method.label || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={method.provider === 'plaid' ? 'Plaid' : 'Stripe'}
                                            size="small"
                                            color={method.provider === 'plaid' ? 'secondary' : 'primary'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={
                                                method.method_type === 'card'
                                                    ? 'Card'
                                                    : method.method_type === 'plaid_ach'
                                                    ? 'ACH'
                                                    : 'Bank Account'
                                            }
                                            size="small"
                                            color={method.method_type === 'card' ? 'primary' : 'info'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{method.institution_name || '—'}</TableCell>
                                    <TableCell>{method.last4 || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={method.is_active ? 'Active' : 'Inactive'}
                                            size="small"
                                            color={method.is_active ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {method.last_used_at
                                            ? new Date(method.last_used_at).toLocaleDateString()
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(method.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(method)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleOpenDelete(method)}
                                            disabled={!method.is_active}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Platform Payment Method</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Stripe Payment Method ID"
                        fullWidth
                        margin="normal"
                        required
                        value={createForm.stripe_payment_method_id}
                        onChange={(e) => setCreateForm({ ...createForm, stripe_payment_method_id: e.target.value })}
                        placeholder="pm_xxx"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Method Type</InputLabel>
                        <Select
                            value={createForm.method_type}
                            label="Method Type"
                            onChange={(e) => setCreateForm({ ...createForm, method_type: e.target.value })}
                        >
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="bank_account">Bank Account</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Purpose</InputLabel>
                        <Select
                            value={createForm.purpose}
                            label="Purpose"
                            onChange={(e) => setCreateForm({ ...createForm, purpose: e.target.value })}
                        >
                            <MenuItem value="flowthrough">Flowthrough</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Label"
                        fullWidth
                        margin="normal"
                        value={createForm.label}
                        onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                        placeholder="Flowthrough Visa ending 4242"
                    />
                    <TextField
                        label="Last 4 Digits"
                        fullWidth
                        margin="normal"
                        value={createForm.last4}
                        onChange={(e) => setCreateForm({ ...createForm, last4: e.target.value.slice(0, 4) })}
                        inputProps={{ maxLength: 4 }}
                        placeholder="4242"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => createMutation.mutate(createForm)}
                        disabled={
                            createMutation.isLoading ||
                            !createForm.stripe_payment_method_id
                        }
                    >
                        {createMutation.isLoading ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Payment Method</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Label"
                        fullWidth
                        margin="normal"
                        value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editForm.is_active}
                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                            />
                        }
                        label="Active"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() =>
                            updateMutation.mutate({
                                methodId: selectedMethod?.id,
                                data: editForm,
                            })
                        }
                        disabled={updateMutation.isLoading}
                    >
                        {updateMutation.isLoading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Deactivate Payment Method</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to deactivate{' '}
                        <strong>{selectedMethod?.label || 'this payment method'}</strong>?
                        This will prevent it from being used for future payout operations.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => deactivateMutation.mutate(selectedMethod?.id)}
                        disabled={deactivateMutation.isLoading}
                    >
                        {deactivateMutation.isLoading ? 'Deactivating...' : 'Deactivate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default PlatformPaymentMethodsSection;
